/**
 * OracleBriefing — full-screen "Chief of Staff" summary.
 *
 * Auto-opens once per admin session (first navigation into /admin/*).
 * Can be reopened at any time by dispatching a `oracle:open-briefing` event
 * from anywhere in the app (the "Briefing" header button uses this).
 *
 * Answers one question in <5 seconds:
 *   "What requires my attention right now?"
 *
 * → Score, colored label ("Steady" / "Watch" / "Attention"), and a Begin
 *   Briefing button that walks the operator through each unresolved item
 *   with Prev/Next navigation and a progress dot strip.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ShieldCheck, Loader2, ArrowRight, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { oracleBriefing } from "../../lib/api";

const ACK_KEY = "oracle-briefing-ack";           // stores UTC date of last acknowledgement
const todayUTC = () => new Date().toISOString().slice(0, 10);
const ackToday = () => { try { localStorage.setItem(ACK_KEY, todayUTC()); } catch { /* noop */ } };
export const OPEN_BRIEFING_EVENT = "oracle:open-briefing";

const scoreColor = (n) => n >= 90 ? "#3FB950" : n >= 70 ? "#E0A526" : "#D92D20";
const scoreLabel = (n) => n >= 90 ? "Steady" : n >= 70 ? "Watch" : "Attention";

export const OracleBriefing = ({ opToken, userToken, identity }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepIdx, setStepIdx] = useState(-1); // -1 = summary; 0..n-1 = walking through items
  const nav = useNavigate();

  // Auto-open ONCE PER DAY (UTC). Acknowledgement persists in localStorage,
  // surviving navigation and refresh; a new day resets it. INFO events,
  // polling and rerenders can never reopen an acknowledged brief.
  useEffect(() => {
    if (!identity) return;
    if (localStorage.getItem(ACK_KEY) === todayUTC()) return;
    setOpen(true);
  }, [identity]);

  // External reopen — any component can dispatch the event to show the briefing.
  useEffect(() => {
    const handler = () => {
      setStepIdx(-1);
      setData(null);       // force re-fetch so the score is live
      setOpen(true);
    };
    window.addEventListener(OPEN_BRIEFING_EVENT, handler);
    return () => window.removeEventListener(OPEN_BRIEFING_EVENT, handler);
  }, []);

  useEffect(() => {
    if (!open || data) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await oracleBriefing(opToken || null, userToken || null);
        if (!cancelled) setData(r);
      } catch { /* silent — user can still close */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open, data, opToken, userToken]);

  const walkItems = useMemo(() => {
    if (!data) return [];
    return [
      ...(data.top?.critical || []),
      ...(data.top?.warning || []),
      ...(data.top?.info || []),
    ];
  }, [data]);

  const onBegin = useCallback(() => setStepIdx(0), []);
  const onNext = useCallback(() => {
    if (stepIdx + 1 >= walkItems.length) {
      ackToday();
      setOpen(false);
      setStepIdx(-1);
    } else {
      setStepIdx(stepIdx + 1);
    }
  }, [stepIdx, walkItems]);
  const onPrev = useCallback(() => {
    if (stepIdx > 0) setStepIdx(stepIdx - 1);
    else setStepIdx(-1);  // back to summary
  }, [stepIdx]);
  const onGoTo = useCallback((href) => {
    ackToday();
    setOpen(false);
    setStepIdx(-1);
    if (href) nav(href);
  }, [nav]);
  const onClose = useCallback(() => {
    ackToday();
    setOpen(false);
    setStepIdx(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const score = data?.health?.score ?? 100;
  const label = data?.health?.label || scoreLabel(score);
  const counts = data?.counts || { critical: 0, warning: 0, info: 0, team: 0 };
  const inWalk = stepIdx >= 0 && walkItems[stepIdx];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="oracle-briefing"
      role="dialog"
      aria-modal="true"
      aria-labelledby="briefing-title"
    >
      <div className="w-[min(560px,100%)] max-h-[90vh] flex flex-col border-2 border-[#D92D20] bg-[#0a0907]">
        <div className="flex items-center justify-between border-b border-[#2D2A26] px-4 py-3">
          <div className="flex items-center gap-2 text-[#D92D20]">
            <ShieldCheck size={14} />
            <h2 id="briefing-title" className="font-mono text-[11px] uppercase tracking-[0.3em]">
              Oracle · Daily Operations Brief
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss briefing"
            data-testid="briefing-close"
            className="text-[#A39E93] hover:text-[#D92D20]"
          >
            <X size={16} />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center gap-2 text-[#A39E93] font-mono text-[12px]">
            <Loader2 size={14} className="animate-spin" /> Assembling briefing…
          </div>
        ) : !data ? (
          <div className="p-8 text-[#A39E93] font-mono text-[12px]">Briefing unavailable.</div>
        ) : inWalk ? (
          // === WALK MODE ===
          <div className="flex-1 overflow-y-auto p-5" data-testid="briefing-walk">
            {/* Progress dots */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A39E93]">
                Item {stepIdx + 1} of {walkItems.length}
              </div>
              <div className="flex items-center gap-1" data-testid="briefing-progress-dots" aria-hidden="true">
                {walkItems.map((it, i) => (
                  <span
                    key={it.id || i}
                    className="w-1.5 h-1.5 rounded-full transition-colors"
                    style={{
                      background:
                        i === stepIdx
                          ? (it.severity === "critical" ? "#D92D20"
                             : it.severity === "warning" ? "#E0A526" : "#3B82F6")
                          : i < stepIdx ? "#454038" : "#1f1c18",
                    }}
                  />
                ))}
              </div>
            </div>
            <div
              className="border-l-2 pl-4 mb-4"
              style={{
                borderLeftColor:
                  inWalk.severity === "critical" ? "#D92D20" :
                  inWalk.severity === "warning"  ? "#E0A526" : "#3B82F6",
              }}
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] mb-1"
                   style={{
                     color: inWalk.severity === "critical" ? "#D92D20" :
                            inWalk.severity === "warning"  ? "#E0A526" : "#3B82F6",
                   }}>
                {inWalk.severity} · {inWalk.category}
              </div>
              <div className="text-[15px] text-[#E4DFD3] font-medium leading-snug">
                {inWalk.title}
              </div>
              {inWalk.message && (
                <div className="text-[12px] text-[#A39E93] mt-1">{inWalk.message}</div>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={onPrev}
                data-testid="briefing-prev"
                className="sm:w-24 px-3 py-2 border border-[#2D2A26] text-[#A39E93] font-mono text-[11px] uppercase tracking-[0.25em] hover:border-[#A39E93] hover:text-[#E4DFD3] flex items-center justify-center gap-1"
              >
                <ArrowLeft size={11} /> Prev
              </button>
              {inWalk.href && (
                <button
                  type="button"
                  onClick={() => onGoTo(inWalk.href)}
                  data-testid="briefing-goto"
                  className="flex-1 px-4 py-2 border border-[#D92D20] text-[#D92D20] font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-[#1a0808] flex items-center justify-center gap-2"
                >
                  Go to resolve <ArrowRight size={12} />
                </button>
              )}
              <button
                type="button"
                onClick={onNext}
                data-testid="briefing-next"
                className="flex-1 px-4 py-2 border border-[#2D2A26] text-[#A39E93] font-mono text-[11px] uppercase tracking-[0.25em] hover:border-[#A39E93] hover:text-[#E4DFD3]"
              >
                {stepIdx + 1 >= walkItems.length ? "Finish" : "Next →"}
              </button>
            </div>
          </div>
        ) : (
          // === SUMMARY MODE ===
          <div className="flex-1 overflow-y-auto p-5" data-testid="briefing-summary">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#A39E93]">Archive Health</div>
                <div className="flex items-baseline gap-3">
                  <div className="font-mono text-[48px] leading-none" style={{ color: scoreColor(score) }} data-testid="briefing-score">
                    {score}%
                  </div>
                  <div
                    className="font-mono text-[11px] uppercase tracking-[0.3em]"
                    style={{ color: scoreColor(score) }}
                    data-testid="briefing-label"
                  >
                    {label}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-right">
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#D92D20]">Critical</div>
                  <div className="font-mono text-[18px] text-[#E4DFD3]">{counts.critical}</div>
                </div>
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#E0A526]">Warnings</div>
                  <div className="font-mono text-[18px] text-[#E4DFD3]">{counts.warning}</div>
                </div>
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#3B82F6]">Info</div>
                  <div className="font-mono text-[18px] text-[#E4DFD3]">{counts.info}</div>
                </div>
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#3FB950]">Last 24h</div>
                  <div className="font-mono text-[18px] text-[#E4DFD3]">{data.delta_24h?.events ?? 0}</div>
                </div>
              </div>
            </div>

            <div className="border-t border-[#2D2A26] pt-3 space-y-1 mb-4">
              {(data.health?.signals || []).map((s, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px] font-mono">
                  {s.ok
                    ? <CheckCircle2 size={12} className="text-[#3FB950] mt-0.5 shrink-0" />
                    : <XCircle size={12} className="text-[#D92D20] mt-0.5 shrink-0" />}
                  <span className={s.ok ? "text-[#A39E93]" : "text-[#E4DFD3]"}>{s.text}</span>
                </div>
              ))}
            </div>

            {data.activity_24h && (
              <div className="border-t border-[#2D2A26] pt-3 mb-5" data-testid="briefing-activity">
                <div className="text-[9px] font-mono uppercase tracking-[0.3em] text-[#A39E93] mb-1.5">
                  Activity last 24h
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-[#A39E93]">
                  <span>Failed access (all)</span>
                  <span className="text-right text-[#E4DFD3]" data-testid="briefing-fails-total">{data.activity_24h.failed_access_total}</span>
                  <span>Credentialed failures</span>
                  <span className="text-right text-[#E4DFD3]" data-testid="briefing-fails-signal">{data.activity_24h.failed_access_credentialed}</span>
                  <span>Verified Owner activity</span>
                  <span className="text-right text-[#3FB950]" data-testid="briefing-owner-activity">{data.activity_24h.verified_owner_actions}</span>
                  <span>Routine info events</span>
                  <span className="text-right text-[#E4DFD3]" data-testid="briefing-routine-info">{data.activity_24h.routine_info_events}</span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={onBegin}
              disabled={walkItems.length === 0}
              data-testid="briefing-begin"
              className="w-full px-4 py-2.5 border-2 border-[#D92D20] text-[#D92D20] font-mono text-[11px] uppercase tracking-[0.3em] hover:bg-[#1a0808] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {walkItems.length === 0 ? "Everything operational" : `Begin Briefing · ${walkItems.length} items`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/** Fire from anywhere to reopen the briefing modal. */
export const openBriefing = () => {
  window.dispatchEvent(new Event(OPEN_BRIEFING_EVENT));
};

export default OracleBriefing;
